// src/pages/FAQ.jsx
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async'; // ※react-helmet-asyncを使用している環境に合わせています
import { motion, AnimatePresence } from 'framer-motion';
import './FAQ.css';

// FAQデータ（カテゴリ別に分類）
const faqCategories = [
    {
        key: 'arrival',
        titleJa: '到着・出発・アクセス',
        titleEn: 'Arrival & Access',
        items: [
            {
                id: 'checkin',
                ja: { q: 'チェックイン、チェックアウトは何時ですか？', a: 'チェックインは15:00から、チェックアウトは10:00となります。' },
                en: { q: 'What are the check-in and check-out times?', a: 'Check-in is from 15:00, and check-out is by 10:00.' }
            },
            {
                id: 'early_late',
                ja: { q: 'アーリーチェックインやレイトチェックアウトは可能ですか？', a: '前後のご予約状況によりますため、確約はできかねますが、ご希望の場合は事前にお問い合わせください。' },
                en: { q: 'Is early check-in or late check-out possible?', a: 'It depends on the booking status before and after your stay. Please contact us in advance if you have a request, though we cannot guarantee availability.' }
            },
            {
                id: 'key',
                ja: { q: '鍵の受け渡しについて教えてください', a: '当施設は電子ロックを採用しているため、対面での鍵の受け渡しはございません。ご予約確定後、入室用の暗証番号をお送りいたします。' },
                en: { q: 'How do I pick up the key?', a: 'We use an electronic lock system, so there is no physical key handover. We will send you the access code after your booking is confirmed.' }
            },
            {
                id: 'parking',
                ja: { q: '駐車場はありますか？', a: '敷地内に3台分ご用意しております。事前予約は不要で、無料でご利用いただけます。' },
                en: { q: 'Is parking available?', a: 'Yes, we have 3 parking spaces available on the premises free of charge. No reservation is required.' }
            },
            {
                id: 'luggage',
                ja: { q: '事前に荷物を送れますか？', a: '可能です。受け取り対応が必要となりますので、必ずご予約時にその旨をご連絡ください。' },
                en: { q: 'Can I send luggage in advance?', a: 'Yes, it is possible. Please contact us at the time of booking so we can arrange to receive it.' }
            }
        ]
    },
    {
        key: 'facility',
        titleJa: '設備・アメニティ',
        titleEn: 'Facilities & Amenities',
        items: [
            {
                id: 'kitchen',
                ja: { q: 'キッチンの設備や調味料はありますか？', a: 'IHコンロ、冷蔵庫、電子レンジ、炊飯器、電気ケトル、調理器具、食器類を一通り完備しております。\n※衛生上の理由から、調味料（塩・胡椒・油など）はご用意しておりませんので、必要に応じてご持参ください。' },
                en: { q: 'What kitchen equipment is available?', a: 'We provide an IH stove, refrigerator, microwave, rice cooker, electric kettle, cookware, and tableware.\n*For hygiene reasons, we do not provide seasonings (salt, pepper, oil, etc.), so please bring them if needed.' }
            },
            {
                id: 'amenity',
                ja: { q: 'バスアメニティは何が用意されていますか？', a: 'シャンプー、コンディショナー、ボディソープ、歯ブラシセット、バスタオル、フェイスタオル、ドライヤーをご用意しております。洗顔料や化粧水などのスキンケア用品はご自身でご持参ください。' },
                en: { q: 'What bath amenities are provided?', a: 'We provide shampoo, conditioner, body soap, toothbrushes, bath towels, face towels, and a hairdryer. Please bring your own skincare products such as face wash and lotion.' }
            },
            {
                id: 'internet',
                ja: { q: '部屋でのインターネットの利用は可能ですか？', a: 'はい、館内全域で無料Wi-Fi（高速光回線）を完備しております。' },
                en: { q: 'Is internet access available?', a: 'Yes, complimentary high-speed Wi-Fi is available throughout the property.' }
            },
            {
                id: 'heating',
                ja: { q: '冬場の寒さが心配ですが、暖房設備はありますか？', a: 'はい、快適にお過ごしいただけるよう、各部屋にエアコンを完備し、冬期は床暖房やこたつもご用意しております。古民家の風情を保ちつつ、断熱改修を行っておりますのでご安心ください。' },
                en: { q: 'Is there heating?', a: 'Yes, each room is equipped with air conditioning. In winter, we also provide floor heating and a "Kotatsu". The house has been renovated with insulation to ensure comfort while maintaining its traditional charm.' }
            },
            {
                id: 'yukata',
                ja: { q: '浴衣の用意はありますか？', a: 'はい、ご宿泊人数分の浴衣をご用意しております。' },
                en: { q: 'Do you provide Yukata (sleepwear)?', a: 'Yes, we provide Yukata for the number of guests staying.' }
            },
            {
                id: 'barrier_free',
                ja: { q: '足が悪いのですが、バリアフリー対応ですか？', a: '当施設は歴史的建築物を保存・活用しているため、玄関の段差や、2階への急な階段がございます。完全なバリアフリーではございませんので、予めご了承ください。寝室は1階にもご用意可能です。' },
                en: { q: 'Is the property wheelchair accessible?', a: 'Since we preserve a historical building, there are steps at the entrance and steep stairs to the 2nd floor. It is not barrier-free. A bedroom can be prepared on the 1st floor upon request.' }
            }
        ]
    },
    {
        key: 'stay_rules',
        titleJa: '滞在のルール・サービス',
        titleEn: 'Rules & Services',
        items: [
            {
                id: 'restaurant_guide',
                ja: { q: '夕食のおすすめレストランは教えてもらえますか？', a: 'はい、もちろんです！ご宿泊予約をいただいた後、お客様のご要望（和食、寿司、居酒屋など）をお伺いし、当宿のスタッフがおすすめのレストランや料亭をご案内いたします。人気店は予約が取りづらいため、お早めにご相談ください。' },
                en: { q: 'Can you recommend restaurants for dinner?', a: 'Yes, absolutely! After booking, please let us know your preferences (Sushi, Washoku, Izakaya, etc.), and our staff will provide personalized recommendations. We highly suggest contacting us early, as popular restaurants book up quickly.' }
            },
            {
                id: 'cultural_experiences',
                // ▼ ここをJSXに変更し、リンクを埋め込んでいます ▼
                ja: {
                    q: '金沢ならではの特別な体験はできますか？',
                    a: <>はい、ご宿泊者様限定で、プライベートな茶道体験、加賀友禅の工房見学、書道、金箔体験などを手配可能です。詳細は<a href="/special-offer" style={{ color: '#B39D82', textDecoration: 'underline' }}>「特別文化体験」のページ</a>をご覧ください。</>
                },
                en: {
                    q: 'Can I book special Kanazawa experiences?',
                    a: <>Yes, we arrange exclusive private experiences for our guests, including Tea Ceremonies, Kaga Yuzen workshops, Zen Calligraphy, and Gold Leaf crafting. Please check our <a href="/en/special-offer" style={{ color: '#B39D82', textDecoration: 'underline' }}>"Exclusive Offers" page</a> for details.</>
                }
            },
            {
                id: 'meals',
                ja: { q: '食事の提供はありますか？', a: '当施設は素泊まり（食事なし）のプランとなります。徒歩圏内に素晴らしい飲食店が多数ございますので、スタッフのおすすめをご活用ください。また、キッチンで自由に料理をしていただくことも可能です。' },
                en: { q: 'Are meals provided?', a: 'We do not provide meals. However, our staff can recommend excellent local restaurants nearby. You are also welcome to cook your own meals in our fully equipped kitchen.' }
            },
            {
                id: 'smoking',
                ja: { q: '喫煙は可能ですか？', a: '建物内は完全禁煙です。屋外（敷地内の庭）に灰皿を設置しておりますので、そちらでのみ喫煙可能です。' },
                en: { q: 'Is smoking allowed?', a: 'The building is strictly non-smoking. Smoking is only permitted in the designated area in the garden where an ashtray is provided.' }
            },
            {
                id: 'cleaning',
                ja: { q: '滞在中の清掃はありますか？', a: 'プライベートな時間を重視するため、滞在中の清掃スタッフの立ち入りはございません。連泊のお客様で清掃やタオルの交換をご希望の場合は、別途ご相談ください（有料となる場合がございます）。' },
                en: { q: 'Is there daily housekeeping service?', a: 'To prioritize your privacy, staff will not enter during your stay. If you require cleaning or towel replacement for long stays, please consult us in advance.' }
            }
        ]
    },
    {
        key: 'booking',
        titleJa: 'ご予約・お支払い',
        titleEn: 'Booking & Payment',
        items: [
            {
                id: 'payment',
                ja: { q: 'クレジットカードでの支払いは可能ですか？', a: 'はい、可能です（Visa, MasterCard, Amex, JCB等）。予約サイトを通じた事前決済にご利用いただけます。' },
                en: { q: 'Do you accept credit cards?', a: 'Yes, we accept major credit cards. Payments are typically handled via the booking platform.' }
            },
            {
                id: 'children',
                ja: { q: '子供は利用できますか？', a: 'はい、ご利用いただけます。\n【添い寝（布団不要）の場合】宿泊料は無料です。予約時に利用人数としてカウントしないでください。\n【布団が必要な場合】大人1名としてカウントし、予約人数に含めてください。' },
                en: { q: 'Can children stay at the property?', a: 'Yes, children are welcome.\n[Co-sleeping (No futon)] Free of charge. Do not include in guest count.\n[Futon required] Please count as an adult and include in total guests.' }
            },
            {
                id: 'pets',
                ja: { q: 'ペットは同伴できますか？', a: '大変申し訳ございませんが、ペットの同伴はお断りしております。' },
                en: { q: 'Are pets allowed?', a: 'We apologize, but pets are not allowed on the property.' }
            }
        ]
    }
];

const FAQItem = ({ item, isEnglish, isOpen, toggleOpen }) => {
    const content = isEnglish ? item.en : item.ja;

    return (
        <motion.div
            className={`faq-item ${isOpen ? 'open' : ''}`}
            initial={false}
        >
            <button className="faq-question" onClick={toggleOpen}>
                <span className="faq-q-text">{content.q}</span>
                <span className="faq-icon">
                    <svg width="12" height="12" viewBox="0 0 12 12">
                        <path d="M6 0V12M0 6H12" stroke="currentColor" strokeWidth="2" />
                    </svg>
                </span>
            </button>
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        key="content"
                        initial="collapsed"
                        animate="open"
                        exit="collapsed"
                        variants={{
                            open: { opacity: 1, height: "auto", marginTop: 15 },
                            collapsed: { opacity: 0, height: 0, marginTop: 0 }
                        }}
                        transition={{ duration: 0.3, ease: [0.04, 0.62, 0.23, 0.98] }}
                    >
                        <div className="faq-answer">
                            {/* ▼ JSX（リンク等）が含まれているか、単なる文字列かでレンダリングを分岐させています ▼ */}
                            {typeof content.a === 'string'
                                ? content.a.split('\n').map((line, i) => <p key={i}>{line}</p>)
                                : <p>{content.a}</p>
                            }
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

const FAQ = () => {
    const location = useLocation();
    const isEnglish = location.pathname.startsWith('/en');
    const canonicalUrl = `https://kanazawa-stay-akebono.com${location.pathname}`;

    const [openId, setOpenId] = useState(null);

    const handleToggle = (id) => {
        setOpenId(openId === id ? null : id);
    };

    const scrollToCategory = (key) => {
        const element = document.getElementById(key);
        if (element) {
            const headerOffset = 100;
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

            window.scrollTo({
                top: offsetPosition,
                behavior: "smooth"
            });
        }
    };

    return (
        <div className="faq-page-container">
            <Helmet>
                <title>{isEnglish ? 'FAQ | Kanazawa Stay Akebono' : 'よくある質問 | 金沢之旅宿 あけぼの'}</title>
                <link rel="canonical" href={canonicalUrl} />
            </Helmet>

            <div className="faq-header">
                <h1 className="faq-title">{isEnglish ? 'Frequently Asked Questions' : 'よくある質問'}</h1>
                <p className="faq-subtitle">
                    {isEnglish ? 'Information about your stay' : 'ご滞在に関するご案内'}
                </p>

                <div className="faq-nav">
                    {faqCategories.map((cat) => (
                        <button
                            key={cat.key}
                            className="faq-nav-btn"
                            onClick={() => scrollToCategory(cat.key)}
                        >
                            {isEnglish ? cat.titleEn : cat.titleJa}
                        </button>
                    ))}
                </div>
            </div>

            <div className="faq-content">
                {faqCategories.map((category) => (
                    <div key={category.key} id={category.key} className="faq-category-section">
                        <h2 className="faq-category-title">
                            {isEnglish ? category.titleEn : category.titleJa}
                        </h2>
                        <div className="faq-list">
                            {category.items.map((item) => (
                                <FAQItem
                                    key={item.id}
                                    item={item}
                                    isEnglish={isEnglish}
                                    isOpen={openId === item.id}
                                    toggleOpen={() => handleToggle(item.id)}
                                />
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            <div className="faq-contact-link">
                <p>
                    {isEnglish
                        ? "Can't find what you're looking for?"
                        : "その他のご質問はございますか？"}
                </p>
                <a href={isEnglish ? "/en/contact" : "/contact"} className="btn-contact-faq">
                    {isEnglish ? "Contact Us" : "お問い合わせ"}
                </a>
            </div>
        </div>
    );
};

export default FAQ;